import { Injectable } from '@angular/core';
import { StorageKeys } from '../../../../enums/storage-keys.enum';
import { UserStatus } from '../../../../enums/user-status.enum';
import { AuthenticatorType } from '../../models/authenticator/authenticator.type';
import { Subject } from 'rxjs';

const AVATAR_CACHE_KEY = 'user_avatar_cache';
const NO_AVATAR_MARKER = 'NO_AVATAR';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private avatarChangedSubject = new Subject<string | null>();
  public avatarChanged$ = this.avatarChangedSubject.asObservable();
  
  private loginSubject = new Subject<any>();
  public onLogin$ = this.loginSubject.asObservable();

  constructor() { }

  getCachedAvatar(): string | null | 'NO_AVATAR' {
    const cached = localStorage.getItem(AVATAR_CACHE_KEY);
    return cached;
  }

  isAvatarCached(): boolean {
    return localStorage.getItem(AVATAR_CACHE_KEY) !== null;
  }

  hasNoAvatar(): boolean {
    return localStorage.getItem(AVATAR_CACHE_KEY) === NO_AVATAR_MARKER;
  }

  setCachedAvatar(avatar: string | null): void {
    if (avatar) {
      localStorage.setItem(AVATAR_CACHE_KEY, avatar);
    } else {
      localStorage.setItem(AVATAR_CACHE_KEY, NO_AVATAR_MARKER);
    }
  }

  clearAvatarCache(): void {
    localStorage.removeItem(AVATAR_CACHE_KEY);
  }

  SetSession(user: any): void {
    const isNewAccount = (user.status as UserStatus) === UserStatus.NewAccount;
    const isGoogle2FA =
      user.twoFactorAuth && user.authenticatorType === AuthenticatorType.Google;
    const isEmail2FA =
      user.twoFactorAuth && user.authenticatorType === AuthenticatorType.Email;

    if (isNewAccount || isGoogle2FA || isEmail2FA) {
      return;
    }

    user.correlationId = crypto.randomUUID();
    localStorage.setItem(StorageKeys.USER, JSON.stringify(user));
    
    this.loginSubject.next(user);
  }

  GetSession(): any {
    const userStr = localStorage.getItem(StorageKeys.USER);
    return userStr ? JSON.parse(userStr) : null;
  }

  notifyAvatarChanged(avatar: string | null): void {
    this.setCachedAvatar(avatar);
    this.avatarChangedSubject.next(avatar);
  }

  hasClaim(claimName: string): boolean {
    const session = this.GetSession();
    if (!session) return false;

    const roleClaims = session.role?.roleClaims;
    if (!Array.isArray(roleClaims)) return false;

    return roleClaims.some(
      (rc: any) => rc?.claim?.name === claimName
    );
  }

  isSystemAdmin(): boolean {
    return this.hasClaim('system_admin_claim');
  }

  getFacilityId(): string | null {
    const session = this.GetSession();
    if (!session) return null;

    // System admin ise facility'e bağlanma
    if (this.isSystemAdmin()) {
      return null;
    }

    const userOrganizations = session.userOrganizations;
    if (!Array.isArray(userOrganizations) || userOrganizations.length === 0) {
      return null;
    }

    // Şimdilik ilk organization
    const firstOrg = userOrganizations[0];
    const userFacilities = firstOrg?.userFacilities;

    if (!Array.isArray(userFacilities) || userFacilities.length === 0) {
      return null;
    }

    // Şimdilik ilk facility
    const firstFacility = userFacilities[0];

    return firstFacility?.facilityId ?? null;
  }

  getOrganizationApproveValue(): boolean | null {
    const session = this.GetSession();
    if (!session) return null;

    // System admin ise facility'e bağlanma
    if (this.isSystemAdmin()) {
      return null;
    }

    const userOrganizations = session.userOrganizations;
    if (!Array.isArray(userOrganizations) || userOrganizations.length === 0) {
      return null;
    }

    // Şimdilik ilk organization
    const firstOrg = userOrganizations[0];
    const needApprove = firstOrg?.isNeedApprove;

    return needApprove ?? null;
  }
}
